import React from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import LogFormShell from "../UI/LogFormShell";
import SourceOriginalNotePanel from "../Timeline/SourceOriginalNotePanel";
import getCaptureFirstTimelineContext from "../Timeline/utils/captureFirstTimelineContext";
import { getCareDocumentOriginalText } from "../../features/careSourceProcessing/careDocumentPresentation";
import {
  getMeaningfulSummary,
  getTherapyPrepCategory,
  toEntryDate,
} from "./reportBuilders/dailyCareReportEntryUtils";

const CATEGORY_LABELS = {
  behavior: "Behavior note",
  sleep: "Sleep note",
  food: "Food note",
  medication: "Medication note",
  mood: "Mood note",
  bathroom: "Bathroom note",
  dailyLog: "Timeline note",
};

const getEntryDate = (entry = {}) => (
  toEntryDate(entry.timestamp || entry.date || entry.createdAt || null)
);

const formatEntryDate = (entry = {}) => {
  const date = getEntryDate(entry);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatEntryTime = (entry = {}) => {
  const date = getEntryDate(entry);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
};

const getEntryTitle = (entry = {}) => (
  String(
    entry.displayTitle
      || entry.title
      || entry.titlePrefix
      || entry.summary
      || CATEGORY_LABELS[getTherapyPrepCategory(entry)]
      || "Timeline note"
  ).trim()
);

const getEntryBody = (entry = {}) => (
  String(
    getMeaningfulSummary(entry)
      || entry.note
      || entry.notes
      || entry.content
      || entry.text
      || entry.description
      || ""
  ).trim()
);

const TherapyPrepSourceEntrySheet = ({
  open,
  entry,
  originalSource = null,
  onClose,
  accent,
  theme,
}) => {
  const category = getTherapyPrepCategory(entry || {}) || "dailyLog";
  const categoryLabel = CATEGORY_LABELS[category] || "Timeline note";
  const accentPrimary = accent?.primary || theme.palette.primary.main;
  const accentInk = accent?.ink || theme.palette.primary.dark;
  const title = entry ? getEntryTitle(entry) : "Timeline note";
  const body = entry ? getEntryBody(entry) : "";
  const dateLabel = entry ? formatEntryDate(entry) : "";
  const timeLabel = entry ? formatEntryTime(entry) : "";
  const entrySourceContext = getCaptureFirstTimelineContext(entry || {}) || {
    originalNote: '', sourceAttachments: [], sourceNoteId: '',
  };
  const linkedSourceContext = getCaptureFirstTimelineContext(originalSource || {}) || {
    sourceAttachments: [],
  };
  const originalNote = getCareDocumentOriginalText(originalSource || {}) || entrySourceContext.originalNote;
  const sourceAttachments = linkedSourceContext.sourceAttachments.length
    ? linkedSourceContext.sourceAttachments
    : entrySourceContext.sourceAttachments;

  return (
    <LogFormShell
      open={open}
      onClose={onClose}
      title="Source note"
      subtitle="From the timeline"
      forceDrawer
      maxWidth="sm"
      surfaceSx={{
        height: { xs: "72vh", sm: "min(72vh, 620px)" },
        maxHeight: { xs: "72vh", sm: "min(72vh, 620px)" },
      }}
      bodySx={{
        px: { xs: 2, sm: 3 },
        pt: 2,
      }}
    >
      <Stack spacing={1.7}>
        <Stack direction="row" spacing={0.8} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.8 }}>
          <Chip
            label={categoryLabel}
            size="small"
            sx={{
              borderRadius: "999px",
              bgcolor: alpha(accentPrimary, 0.12),
              color: accentInk,
              fontWeight: 800,
            }}
          />
          {dateLabel ? (
            <Typography sx={{ color: "text.secondary", fontSize: "0.88rem", fontWeight: 700 }}>
              {[dateLabel, timeLabel].filter(Boolean).join(" · ")}
            </Typography>
          ) : null}
        </Stack>

        <Box
          sx={{
            p: 2,
            borderRadius: "16px",
            border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
            bgcolor: alpha(accentPrimary, 0.045),
          }}
        >
          <Typography
            component="h2"
            sx={{
              m: 0,
              color: theme.palette.text.primary,
              fontSize: { xs: "1.08rem", sm: "1.18rem" },
              fontWeight: 850,
              lineHeight: 1.25,
            }}
          >
            {title}
          </Typography>
          {body ? (
            <Typography sx={{ mt: 1.2, color: "text.secondary", fontSize: "0.95rem", lineHeight: 1.65 }}>
              {body}
            </Typography>
          ) : (
            <Typography sx={{ mt: 1.2, color: "text.secondary", fontSize: "0.95rem", lineHeight: 1.65 }}>
              This timeline note does not have additional text saved.
            </Typography>
          )}
        </Box>
        <SourceOriginalNotePanel
          originalNote={originalNote}
          sourceAttachments={sourceAttachments}
          sourceNoteId={entrySourceContext.sourceNoteId || originalSource?.id || ''}
          variant="inline"
          showSourceReviewActions={false}
          closedLabelOverride="View original captured note"
          openLabelOverride="Hide original captured note"
        />
      </Stack>
    </LogFormShell>
  );
};

export default TherapyPrepSourceEntrySheet;
